import { Link, useNavigate, useParams } from "react-router-dom";
import { decodeUrlParam } from "../../utils/urlUtils";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { useCallback, useEffect, useState } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "./dropdownStyles.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import StaffTabBar from "./staffTabBar.js";
import APIs from "../../Constants/apis.js";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import confirmUnsavedChangesSwal from "../../utils/confirmUnsavedChangesSwal";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import UpdateShimmer from "../../components/CustomComponents/preLoad/UpdateShimmer.jsx";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector";

/** Username is set from email in the staff UI — show email wording for duplicate-identity API errors. */
function formatStaffIdentityError(message) {
  const text = String(message ?? "").trim();
  if (!text) return text;
  // API often returns username wording; staff username is the email in the UI
  if (
    /(user\s*name|username)/i.test(text) &&
    (/already\s*exist/i.test(text) || /already\s+a\s+user/i.test(text) || /with\s+given\s+username/i.test(text))
  ) {
    return "Email already exists";
  }
  return text;
}

function UpdateStaff() {
  const { UserName: encodedUserName } = useParams();
  const UserName = decodeUrlParam(encodedUserName);
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;

  // Get profileData from Redux
  const { profileData } = useSelector((state) => state.auth);
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Select appropriate dropdown styles based on theme
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const [roles, setRoles] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [countryCallCodeMap, setCountryCallCodeMap] = useState({});
  const [companyCallCode, setCompanyCallCode] = useState("");

  const formatDialCode = useCallback((raw) => {
    if (!raw || typeof raw !== "string") return "";
    const trimmed = raw.trim();
    if (!trimmed) return "";
    return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
  }, []);

  const companyCountry =
    profileData?.compCountry || profileData?.CompCountry || "";
  const companyDialCode = formatDialCode(
    companyCallCode ||
      profileData?.callCode ||
      profileData?.CallCode ||
      countryCallCodeMap[companyCountry] ||
      countryCallCodeMap[(companyCountry || "").toUpperCase()] ||
      ""
  );

  const [staffData, setStaffData] = useState({
    userName: "",
    firstName: "",
    lastName: "",
    email: "",
    country: "", 
    city: "",
    phoneNo: "",
    passCode: "",
    ratePerHour: null,
    active: false,
    userRoles: null,
    managerUserName: "",
    staffImage: "",
    isAllowOverTime: false,
    isFullTimeStaff: false,
    isNewHireStaff: false,
    overTimeHourlyRate: null,
    isSalaryStaff: false,
    salaryType: "Weekly",
    salary: null,
    weeklyHours: null,
    monthlyHours: null,
    isBreakAllowed: false,
    breakMinutes: null,
  });
  const [initialLoading, setInitialLoading] = useState(true);

  // Errors state for real-time validation
  const [errors, setErrors] = useState({});

  // Image state
  const [imageState, setImageState] = useState({
    staffImage: "",
    imageFile: null,
  });
  // Initial loaded data snapshot – set once after fetch, used to detect changes and avoid unnecessary save/API call
  const [initialLoadedDataSnapshot, setInitialLoadedDataSnapshot] = useState(null);

  const normalizeStaffImagePath = useCallback((imagePath) => {
    if (!imagePath) return "";
    const imageWithoutQuery = String(imagePath).split("?")[0];
    if (imageWithoutQuery.startsWith("http")) {
      try {
        const urlObj = new URL(imageWithoutQuery);
        return urlObj.pathname.replace(/^\/+/, "");
      } catch (err) {
        return imageWithoutQuery.replace(/^\/+/, "");
      }
    }
    return imageWithoutQuery.replace(/^\/+/, "");
  }, []);

  const resolveStaffImagePathOnLoad = useCallback(async (imagePath) => {
    const normalizedPath = normalizeStaffImagePath(imagePath);
    if (!normalizedPath) return "";

    // Already in expected tenant folder format (uploads/{CompID}/file.ext)
    if (/^uploads\/\d+\/.+/i.test(normalizedPath)) {
      return normalizedPath;
    }

    // Legacy format fallback (uploads/file.ext) -> try to resolve from company media
    const legacyMatch = normalizedPath.match(/^uploads\/([^/]+)$/i);
    if (legacyMatch) {
      const legacyFileName = legacyMatch[1];
      try {
        const mediaResponse = await axiosInstance.get(APIs.GET_COMPANY_MEDIA_LIST, {
          params: {
            search: legacyFileName,
            pageNumber: 1,
            pageSize: 20,
          },
        });
        const mediaItems = mediaResponse?.data?.data?.items || [];
        const matchedMedia = mediaItems.find((item) => {
          const originalFileName = String(item?.originalFileName || "").toLowerCase();
          const filePath = normalizeStaffImagePath(item?.filePath || "").toLowerCase();
          const fileUrlPath = normalizeStaffImagePath(item?.fileUrl || "").toLowerCase();
          const legacyFileNameLower = legacyFileName.toLowerCase();
          return (
            originalFileName === legacyFileNameLower ||
            filePath.endsWith(`/${legacyFileNameLower}`) ||
            fileUrlPath.endsWith(`/${legacyFileNameLower}`)
          );
        });
        if (matchedMedia?.filePath) {
          return normalizeStaffImagePath(matchedMedia.filePath);
        }
      } catch (mediaErr) {
        // Keep legacy path if media lookup fails; do not block staff load.
      }
    }

    return normalizedPath;
  }, [normalizeStaffImagePath]);

  // Snapshot keys in fixed order so JSON.stringify is deterministic (avoids false "dirty" on scroll/re-render)
  const SNAPSHOT_KEYS = [
    "active", "breakMinutes", "city", "country", "email", "firstName", "isAllowOverTime",
    "isBreakAllowed", "isFullTimeStaff", "isNewHireStaff", "isSalaryStaff", "lastName",
    "managerUserName", "monthlyHours", "overTimeHourlyRate", "passCode", "phoneNo",
    "ratePerHour", "salary", "salaryType", "staffImage", "userName", "userRoles",
    "weeklyHours", "imageFileName"
  ];

  const NUMERIC_SNAPSHOT_KEYS = ["breakMinutes", "monthlyHours", "overTimeHourlyRate", "ratePerHour", "salary", "weeklyHours"];

  const buildSnapshot = useCallback(
    ({ data, roleValue, managerValue, imagePath, imageFile }) => {
      const normalizedImage = normalizeStaffImagePath(imagePath);
      const normalizedData = {
        ...data,
        userRoles: roleValue ?? null,
        managerUserName: (managerValue ?? "") === null ? "" : (managerValue || ""),
        staffImage: normalizedImage,
        imageFileName: imageFile?.name || "",
      };
      const ordered = {};
      SNAPSHOT_KEYS.forEach((key) => {
        if (normalizedData.hasOwnProperty(key)) {
          let v = normalizedData[key];
          if (NUMERIC_SNAPSHOT_KEYS.includes(key)) {
            if (v === "" || v === undefined) v = null;
            else if (typeof v === "string") {
              const n = Number(v);
              v = Number.isNaN(n) ? null : n;
            } else if (typeof v === "number" && Number.isNaN(v)) v = null;
          }
          ordered[key] = v;
        }
      });
      return JSON.stringify(ordered);
    },
    [normalizeStaffImagePath]
  );

  const getCurrentSnapshot = useCallback(
    (overrides = {}) => {
      return buildSnapshot({
        data: overrides.data || staffData,
        roleValue:
          overrides.roleValue !== undefined
            ? overrides.roleValue
            : selectedRole?.value ?? null,
        managerValue:
          overrides.managerValue !== undefined
            ? overrides.managerValue
            : selectedManager?.value || "",
        imagePath:
          overrides.imagePath !== undefined
            ? overrides.imagePath
            : imageState.staffImage,
        imageFile:
          overrides.imageFile !== undefined
            ? overrides.imageFile
            : imageState.imageFile,
      });
    },
    [buildSnapshot, imageState.imageFile, imageState.staffImage, selectedManager?.value, selectedRole?.value, staffData]
  );

  const FetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
        },
      });
      const roleData = response.data.data || response.data;
      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
    } catch (err) {
      showErrorToast("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const FetchManagers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID,
          PageNumber: 1,
          PageSize: 1000, // Fetch all for dropdown
          Search: "",
          SortBy: "UserName",
          SortOrder: "ASC"
        },
      });

      const staffData = response.data.data || response.data;

      if (Array.isArray(staffData)) {
        // Filter out any null or undefined items from the array
        const validStaffs = staffData.filter((staff) => {
          if (!staff || staff.userName === UserName || !staff.active) {
            return false;
          }

          const staffRoleLabel = String(staff.r_Title || staff.roleName || "").toLowerCase();
          const isRiderByName = staffRoleLabel.includes("rider");
          return !isRiderByName;
        });
        const managersOptions = validStaffs.map((staff) => ({
          value: staff.userName,
          label: `${staff.firstName} ${staff.lastName || ''} (${staff.userName})`,
        }));
        setManagers(managersOptions);
      } else {
        // Unexpected API response format silently
        setManagers([]);
      }
    } catch (err) {
      showErrorToast("Failed to fetch managers");
    } finally {
      setLoading(false);
    }
  }, [CompID, UserName]);

  const FetchStaffData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID,
          UserName,
        },
      });
      const staff = response.data.data || response.data;
      const resolvedStaffImagePath = await resolveStaffImagePathOnLoad(staff.staffImage || "");

      const defaultCountry = profileData?.compCountry || 
                            profileData?.CompCountry || 
                            "";

      const roleId = staff.comRolePermission_ID;
      let roleOption = roles.find((r) => r.value === roleId);
      if (!roleOption && roleId) {
        roleOption = { value: roleId, label: `Role ID: ${roleId}` };
      }
      setSelectedRole(roleOption);

      const defaultCity = staff.city || profileData?.compCity || profileData?.CompCity || "";

      let managerOption = null;
      if (staff.managerUserName) {
        managerOption = managers.find((m) => m.value === staff.managerUserName);
        if (!managerOption) {
          // Fallback if not in list
          try {
            const managerResponse = await axiosInstance.get(APIs.GET_STAFF_DATA, {
              params: { CompID, UserName: staff.managerUserName }
            });
            const managerStaff = managerResponse.data.data || managerResponse.data;
            managerOption = {
              value: staff.managerUserName,
              label: `${managerStaff.firstName || ''} ${managerStaff.lastName || ''} (${staff.managerUserName})`
            };
          } catch (fallbackErr) {
            managerOption = {
              value: staff.managerUserName,
              label: `Manager: ${staff.managerUserName}`
            };
          }
        }
      }
      setSelectedManager(managerOption);

      setStaffData({
        userName: staff.userName || "",
        firstName: staff.firstName || "",
        lastName: staff.lastName || "",
        email: staff.email || "",
        country: defaultCountry,
        city: defaultCity,
        phoneNo: staff.phoneNo || "",
        passCode: staff.passCode || "",
        ratePerHour: staff.ratePerHour ?? staff.RatePerHour ?? null,
        active: staff.active || false,
        userRoles: roleId || null,
        managerUserName: staff.managerUserName || "",
        isBreakAllowed: staff.isBreakAllowed || false,
        breakMinutes: staff.breakMinutes || null,
        staffImage: resolvedStaffImagePath,
        isAllowOverTime: staff.isAllowOverTime || false,
        isFullTimeStaff: staff.isFullTimeStaff || false,
        isNewHireStaff: staff.isNewHireStaff || false,
        overTimeHourlyRate: staff.overTimeHourlyRate || null,
        isSalaryStaff: staff.isSalaryStaff || false,
        salaryType: staff.salaryType || "Weekly",
        salary: staff.salary || null,
        weeklyHours: staff.weeklyHours ?? null,
        monthlyHours: staff.monthlyHours ?? null,
      });

      // Set image state
      setImageState({
        staffImage: resolvedStaffImagePath,
        imageFile: null,
      });
      setInitialLoadedDataSnapshot(
        buildSnapshot({
          data: {
            userName: staff.userName || "",
            firstName: staff.firstName || "",
            lastName: staff.lastName || "",
            email: staff.email || "",
            country: defaultCountry,
            city: defaultCity,
            phoneNo: staff.phoneNo || "",
            passCode: staff.passCode || "",
            ratePerHour: staff.ratePerHour ?? staff.RatePerHour ?? null,
            active: staff.active || false,
            userRoles: roleId || null,
            managerUserName: staff.managerUserName || "",
            staffImage: resolvedStaffImagePath,
            isAllowOverTime: staff.isAllowOverTime || false,
            isFullTimeStaff: staff.isFullTimeStaff || false,
            isNewHireStaff: staff.isNewHireStaff || false,
            overTimeHourlyRate: staff.overTimeHourlyRate || null,
            isSalaryStaff: staff.isSalaryStaff || false,
            salaryType: staff.salaryType || "Weekly",
            salary: staff.salary || null,
            weeklyHours: staff.weeklyHours ?? null,
            monthlyHours: staff.monthlyHours ?? null,
            isBreakAllowed: staff.isBreakAllowed || false,
            breakMinutes: staff.breakMinutes || null,
          },
          roleValue: roleId || null,
          managerValue: staff.managerUserName || "",
          imagePath: resolvedStaffImagePath,
          imageFile: null,
        })
      );
    } catch (err) {
      showErrorToast("Failed to fetch staff data");
      console.error("Fetch staff error:", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [CompID, UserName, roles, managers, profileData, buildSnapshot, resolveStaffImagePathOnLoad]);

  // Reset and reload when UserName changes
  useEffect(() => {
    let cancelled = false;
    const bootstrapDialCode = async () => {
      try {
        const [countriesRes, profileRes] = await Promise.all([
          axiosInstance.get(APIs.GET_COUNTRIES),
          axiosInstance.get(APIs.GET_COMPANY_PROFILE),
        ]);
        if (cancelled) return;

        const list = countriesRes.data?.data || countriesRes.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((country) => {
          const callCode = country.callCode ?? country.CallCode ?? "";
          const code = country.code ?? country.Code ?? "";
          const code2 = country.code2 ?? country.Code2 ?? "";
          const name = country.name ?? country.Name ?? "";
          if (code) map[code] = callCode;
          if (code2) map[code2] = callCode;
          if (name) map[name] = callCode;
        });
        setCountryCallCodeMap(map);

        const profile = profileRes.data?.data || {};
        const profileCallCode = profile.callCode || profile.CallCode || "";
        if (profileCallCode) setCompanyCallCode(profileCallCode);
      } catch {
        // Dial code is optional UI adornment; fail silently
      }
    };
    bootstrapDialCode();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Reset loading state
    setInitialLoading(true);
    setLoading(false);
    
    // Clear previous data
    setStaffData({
      userName: "",
      firstName: "",
      lastName: "",
      email: "",
      country: "", 
      city: "",
      phoneNo: "",
      passCode: "",
      ratePerHour: null,
      active: false,
      userRoles: null,
      managerUserName: "",
      staffImage: "",
      isAllowOverTime: false,
      isFullTimeStaff: false,
      isNewHireStaff: false,
      overTimeHourlyRate: null,
      isSalaryStaff: false,
      salaryType: "Weekly",
      salary: null,
      weeklyHours: null,
      monthlyHours: null,
      isBreakAllowed: false,
      breakMinutes: null,
    });
    setSelectedRole(null);
    setSelectedManager(null);
    setErrors({});
    setInitialLoadedDataSnapshot(null);
    
    // Fetch roles and managers first
    FetchRoles();
    FetchManagers();
  }, [UserName, FetchRoles, FetchManagers]);

  // Fetch staff data after roles and managers are loaded
  useEffect(() => {
    if ((roles.length > 0 || managers.length > 0) && UserName && CompID) {
      FetchStaffData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.length, managers.length, UserName, CompID]);

  // Validation functions
  const validateFirstName = (value) => {
    if (!value.trim()) return "First name is required";
    if (value.length < 2) return "First name must be at least 2 characters long";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validateCountry = (value) => {
    if (!value) return "Country is required";
    return "";
  };

  const validateCity = (value) => {
    if (!value) return "City is required";
    return "";
  };

  const validatePassCode = (value) => {
    if (!value.trim()) return "Pass code is required";
    return "";
  };

  const validateRole = (value) => {
    if (!value) return "User role is required";
    return "";
  };

  const validateBreakMinutes = (value, isBreakAllowed) => {
    if (!isBreakAllowed) return "";
    if (!value || value <= 0) return "Break time must be a positive number";
    return "";
  };

  const validateOverTimeRate = (value, isAllowOverTime, ratePerHour) => {
    if (!isAllowOverTime) return "";
    let numValue = value ? parseFloat(value) : null;
    if (numValue === null) {
      numValue = ratePerHour ? parseFloat(ratePerHour) : null;
    }
    if (!numValue || numValue <= 0) return "Overtime rate must be a positive number";
    return "";
  };

  const validateSalary = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType === "Hourly") return "";
    if (!value || parseFloat(value) <= 0)
      return "Salary must be a positive number";
    return "";
  };

  const validateSalaryType = (value, isSalaryStaff) => {
    if (!isSalaryStaff) return "";
    if (!value || value.trim() === "")
      return "Salary type is required";
    return "";
  };

  const validateWeeklyHours = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Weekly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Weekly hours must be a positive number";
    return "";
  };

  const validateMonthlyHours = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Monthly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Monthly hours must be a positive number";
    return "";
  };

  const validateRatePerHour = (value, isSalaryStaff, salaryType) => {
    if (!isSalaryStaff || salaryType !== "Hourly") return "";
    const num = value != null && value !== "" ? parseFloat(value) : null;
    if (num === null || num <= 0) return "Hourly rate must be a positive number";
    return "";
  };

  // Generic onChange handler with validation
  const handleInputChange = (field, value, validator, extraValidator = null) => {
    setStaffData(prev => ({ ...prev, [field]: value }));

    // Clear previous error
    setErrors(prev => ({ ...prev, [field]: "" }));

    // Validate immediately
    let error = validator ? validator(value) : "";
    if (extraValidator) {
      const extraError = extraValidator(value);
      if (extraError) error = extraError;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // On blur validation for selects
  const handleSelectChange = (field, value, validator) => {
    let error = validator ? validator(value) : "";
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleCountryCityChange = (updatedData) => {
    setStaffData(prev => ({
      ...prev,
      country: updatedData.country || prev.country,
      city: updatedData.city || "",
    }));
    

    if (updatedData.city !== undefined) {
      const error = validateCity(updatedData.city);
      setErrors(prev => ({ ...prev, city: error }));
    }
    if (updatedData.country !== undefined) {
      const error = validateCountry(updatedData.country);
      setErrors(prev => ({ ...prev, country: error }));
    }
  };


  const validateForm = () => {
    const newErrors = {};

    newErrors.firstName = validateFirstName(staffData.firstName);
    newErrors.email = validateEmail(staffData.email);
    newErrors.country = validateCountry(staffData.country);
    newErrors.city = validateCity(staffData.city);
    newErrors.passCode = validatePassCode(staffData.passCode);
    newErrors.userRoles = validateRole(selectedRole?.value);

    newErrors.breakMinutes = validateBreakMinutes(staffData.breakMinutes, staffData.isBreakAllowed);

    // Validate overtime for both salary and hourly staff
    newErrors.overTimeHourlyRate = validateOverTimeRate(staffData.overTimeHourlyRate, staffData.isAllowOverTime, staffData.ratePerHour);

    // Validate based on staff type
    if (staffData.isSalaryStaff) {
      newErrors.salary = validateSalary(staffData.salary, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.salaryType = validateSalaryType(staffData.salaryType, staffData.isSalaryStaff);
      newErrors.weeklyHours = validateWeeklyHours(staffData.weeklyHours, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.monthlyHours = validateMonthlyHours(staffData.monthlyHours, staffData.isSalaryStaff, staffData.salaryType);
      newErrors.ratePerHour = validateRatePerHour(staffData.ratePerHour, staffData.isSalaryStaff, staffData.salaryType);
    }

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(error => error !== "");
    if (hasErrors) {
      showErrorToast("Please fix the errors below before submitting");
      return false;
    }

    return true;
  };


  const handleImageChange = (url, file) => {
    setImageState({
      staffImage: url,
      imageFile: file,
    });
  };


  const handleRemoveImage = () => {
    setImageState({
      staffImage: "",
      imageFile: null,
    });
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (saving) {
      return false;
    }

    // No changes from initial loaded data – skip API (for Save stay; for Save and Exit we'll navigate below)
    if (initialLoadedDataSnapshot !== null && getCurrentSnapshot() === initialLoadedDataSnapshot) {
      if (exitAfterSave) {
        navigate("/staff/manageStaff");
      }
      return true;
    }

    if (!validateForm()) {
      return false;
    }

    let {
      userName,
      firstName,
      lastName,
      email,
      country,
      city,
      phoneNo,
      passCode,
      ratePerHour,
      active,
      isBreakAllowed,
      breakMinutes,
      isAllowOverTime,
      isFullTimeStaff,
      isNewHireStaff,
      overTimeHourlyRate,
      isSalaryStaff,
      salaryType,
      salary,
      weeklyHours,
      monthlyHours,
    } = staffData;

    if (isAllowOverTime && (!overTimeHourlyRate || overTimeHourlyRate === null)) {
      overTimeHourlyRate = ratePerHour ? parseFloat(ratePerHour) : 0;
    }

    try {
      setSaving(true);

      let finalStaffImage = null;
      const baseURL = axiosInstance.defaults.baseURL || "";

      if (imageState.imageFile) {
        try {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          imageFormData.append("UserName", userName);
          imageFormData.append("CompID", CompID);

          const imageResponse = await axiosInstance.post(
            APIs.UPDATE_STAFF_IMAGE,
            imageFormData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              params: {
                UserName: userName,
                CompID: CompID,
              },
            }
          );

          if (imageResponse.data?.status && imageResponse.data?.data?.filePath) {
            const relativePath = imageResponse.data.data.filePath;
            finalStaffImage = relativePath; // Store relative path for API
            
            // Construct full URL by prepending baseURL (matching backend logic)
            // Backend service does: _baseUrl + staffImagePath
            // Handle both cases: baseURL with/without trailing slash, path with/without leading slash
            let fullImageUrl;
            if (relativePath.startsWith("http")) {
              fullImageUrl = relativePath;
            } else {
              const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
              const pathClean = relativePath.startsWith("/") ? relativePath : `/${relativePath}`;
              fullImageUrl = `${baseUrlClean}${pathClean}`;
            }
            
            // Update image state with full URL for display
            const displayUrl = `${fullImageUrl}?t=${Date.now()}`;
            setImageState({ staffImage: displayUrl, imageFile: null });
          } else {
            throw new Error(imageResponse.data?.message || "Image upload failed");
          }
        } catch (imageError) {
          // If image upload fails, preserve the original image state
          console.error("Image upload error:", imageError);
          showErrorToast(imageError.response?.data?.message || "Failed to upload image. Please try again.");
          setSaving(false);
          return false; // Stop here if image upload fails
        }
      } else if (!imageState.staffImage || imageState.staffImage === "") {
        finalStaffImage = null;
      } else {
        // Extract relative path from full URL if it's already a full URL
        const currentImage = imageState.staffImage.split("?")[0];
        if (currentImage.startsWith("http")) {
          // Extract relative path from full URL
          const urlObj = new URL(currentImage);
          finalStaffImage = urlObj.pathname.startsWith("/") ? urlObj.pathname.substring(1) : urlObj.pathname;
        } else {
          finalStaffImage = currentImage;
        }
      }

      const response = await axiosInstance.put(APIs.UPDATE_STAFF, {
        UserName: userName,
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Country: country,
        City: city,
        PhoneNo: phoneNo,
        PassCode: passCode,
        RatePerHour: isSalaryStaff && salaryType === "Hourly" && ratePerHour !== null ? parseFloat(ratePerHour) : null,
        Active: active,
        Password: "",
        CompID: CompID,
        ComRolePermission_ID: selectedRole?.value || null,
        ManagerUserName: selectedManager?.value || "",
        IsBreakAllowed: isBreakAllowed,
        BreakMinutes: breakMinutes ? parseInt(breakMinutes) : null,
        StaffImage: finalStaffImage || null,
        IsAllowOverTime: isAllowOverTime,
        ISFullTimeStaff: isFullTimeStaff,
        ISNewHireStaff: isNewHireStaff,
        OverTimeHourlyRate: overTimeHourlyRate ? parseFloat(overTimeHourlyRate) : 0,
        IsSalaryStaff: isSalaryStaff,
        SalaryType: isSalaryStaff ? salaryType : "",
        Salary: isSalaryStaff && salary !== null ? parseFloat(salary) : null,
        WeeklyHours: isSalaryStaff && salaryType === "Weekly" && weeklyHours != null ? parseFloat(weeklyHours) : null,
        MonthlyHours: isSalaryStaff && salaryType === "Monthly" && monthlyHours != null ? parseFloat(monthlyHours) : null,
      });

      if (response.data.status) {
        showSuccessToast("Staff updated successfully!");
        // Keep relative path in state so snapshot vs ImageUpload stay in sync (full URL breaks dirty checks).
        if (finalStaffImage) {
          setImageState({ staffImage: finalStaffImage, imageFile: null });
          setStaffData((prev) => ({ ...prev, staffImage: finalStaffImage }));
        } else {
          setImageState({ staffImage: "", imageFile: null });
          setStaffData((prev) => ({ ...prev, staffImage: "" }));
        }
        setInitialLoadedDataSnapshot(
          getCurrentSnapshot({
            imagePath: finalStaffImage || "",
            imageFile: null,
          })
        );
        if (exitAfterSave) {
          navigate("/staff/manageStaff");
        }
        return true;
      } else {
        showErrorToast(
          formatStaffIdentityError(response.data.message || response.data.Message) ||
            "Failed to update staff"
        );
        return false;
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join(", ")
          : "An error occurred while updating the staff");
      showErrorToast(formatStaffIdentityError(errorMessage));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTabChangeBeforeNavigate = useCallback(
    async (clickedTabId, clickedTabLink) => {
      if (saving || loading || initialLoading) {
        return false;
      }

      // No changes from initial loaded data – allow switch without warning
      if (initialLoadedDataSnapshot === null || getCurrentSnapshot() === initialLoadedDataSnapshot) {
        return true;
      }

      const result = await confirmUnsavedChangesSwal();

      if (!result) {
        return false;
      }
      if (result === "discard") {
        return true;
      }
      if (result === "save") {
        const success = await handleSubmit({ exitAfterSave: false });
        if (success && clickedTabLink) {
          navigate(clickedTabLink);
        }
        return false;
      }
      return false;
    },
    [getCurrentSnapshot, handleSubmit, initialLoading, initialLoadedDataSnapshot, loading, navigate, saving]
  );

  const cardStyle = {
    backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
    border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6",
    color: isDarkMode ? "#ffffff" : "#000000",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
  };

  const sectionHeaderStyle = {
    color: isDarkMode ? "#ffffff" : "#495057",
    fontSize: "1.1rem",
    fontWeight: "600",
    marginBottom: "1.5rem",
    paddingBottom: "0.5rem",
    borderBottom: `2px solid ${themeColor}`,
  };

  // Error message style
  const errorStyle = {
    color: '#dc3545',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
    fontWeight: '400',
  };

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/staff/manageStaff">Manage Staff</Link>
            </li>
            <li className="breadcrumb-item active">Update Staff</li>
          </ol>
          <h1 className="page-header mb-0">
            Update Staff – {[staffData.firstName, staffData.lastName].filter(Boolean).join(" ").trim() || UserName} ({UserName})
          </h1>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2 flex-wrap justify-content-end">
          <button
            type="button"
            onClick={() => navigate("/staff/manageStaff")}
            disabled={saving}
            className="btn px-4"
            title="Leave without saving"
            style={{
              background: "#dc3545",
              border: "none",
              color: "#fff",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.35)",
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSubmit({ exitAfterSave: false })}
            disabled={saving}
            className="btn px-4"
            style={{
              background: isDarkMode ? "#495057" : "#6c757d",
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              cursor: saving ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
              }
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit({ exitAfterSave: true })}
            disabled={saving}
            className="btn px-4"
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: saving ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }
            }}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-white"></i>
            {saving ? "Saving..." : "Save and Exit"}
          </button>
        </div>
      </div>
      <GridSectionLoader loading={(loading || saving) && !initialLoading} minHeight="280px">
<div className="card border-0">
        <StaffTabBar
          activeTab="updateInfo"
          useTab="false"
          UserName={UserName}
          onBeforeTabChange={handleTabChangeBeforeNavigate}
        />
        <Panel className="m-3">
          <PanelHeader>Staff Information</PanelHeader>
          <PanelBody>
            {initialLoading ? (
              <UpdateShimmer />
            ) : (
              <div className="row g-4">
                {/* Top Row: Image Section and Toggles */}
                <div className="row mt-3">
                  <div className="col-md-6">
                    <div className="text-center p-4" style={cardStyle}>
                      <ImageUpload
                        imageUrl={imageState.staffImage}
                        imageFile={imageState.imageFile}
                        setImage={handleImageChange}
                        onRemove={handleRemoveImage}
                        className=""
                        centerButton={true}
                        useGrid={false}
                      />
                      <p className="text-muted mt-3 mb-0" style={{ fontSize: "0.9rem" , color: isDarkMode ? "#ffffff" : "#495057" }}>
                        <strong>Full Name:</strong> {staffData.firstName} {staffData.lastName}
                      </p>
                      <p className="text-muted" style={{ fontSize: "0.9rem", color: isDarkMode ? "#ffffff" : "#495057" }}>
                        <strong>Email:</strong> {staffData.email}
                      </p>
                     
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-4 mb-3 mt-3" style={cardStyle}>
                      <h6 style={sectionHeaderStyle}>Status</h6>
                      <div className="row g-3">
                        <div className="col-6">
                          <ToggleSwitch
                            isChecked={staffData.active}
                            onToggle={(checked) =>
                              setStaffData({ ...staffData, active: checked })
                            }
                            label="Active"
                            name="active"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col-6">
            
                        </div>
                      </div>
                      <div className="row g-3 mt-3">
                        <div className="col-6">
                          <ToggleSwitch
                            isChecked={staffData.isFullTimeStaff}
                            onToggle={(checked) =>
                              setStaffData({ ...staffData, isFullTimeStaff: checked })
                            }
                            label="Full Time Staff"
                            name="fullTime"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col-6">
                          <ToggleSwitch
                            isChecked={staffData.isNewHireStaff}
                            onToggle={(checked) =>
                              setStaffData({ ...staffData, isNewHireStaff: checked })
                            }
                            label="New Hire Staff"
                            name="newHire"
                            themeColor={themeColor}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Basic Information Card */}
                <div className="col-md-6">
                  <div className="p-4" style={cardStyle}>
                    <h6 style={sectionHeaderStyle}>Basic Information</h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <FormInput
                          label="First Name *"
                          placeHolder="Enter First Name"
                          type="text"
                          value={staffData.firstName}
                          onchange={(e) =>
                            handleInputChange("firstName", e.target.value, validateFirstName)
                          }
                        />
                        {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Last Name"
                          placeHolder="Enter Last Name"
                          type="text"
                          value={staffData.lastName}
                          onchange={(e) =>
                            setStaffData({
                              ...staffData,
                              lastName: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Email *"
                          placeHolder="Enter Email"
                          type="email"
                          value={staffData.email}
                          readOnly={true}
                        />
                        {errors.email && <div style={errorStyle}>{errors.email}</div>}
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Phone Number"
                          placeHolder="Enter Phone Number"
                          type="text"
                          value={staffData.phoneNo}
                          onchange={(e) =>
                            setStaffData({
                              ...staffData,
                              phoneNo: e.target.value,
                            })
                          }
                          prepend={companyDialCode || undefined}
                        />
                      </div>
                    </div>
                  </div>
                </div>

         
                <div className="col-md-6">
                  <div className="p-4 mb-3" style={cardStyle}>
                    <h6 style={sectionHeaderStyle}>Location & Role</h6>
                    <div className="row g-3">
     
                      <div className="col-12">
                        <CountryCitySelector
                          selectedCountry={staffData.country || profileData?.compCountry || profileData?.CompCountry}
                          selectedCity={staffData.city}
                          setSelectedCity={(city) => {
                            setStaffData(prev => ({ ...prev, city: city || "" }));
                            const error = validateCity(city || "");
                            setErrors(prev => ({ ...prev, city: error }));
                          }}
                          onChange={handleCountryCityChange}
                          setLoading={null}
                          showCountryField={false}
                          showCityField={true}
                          cityLabel="City *"
                          cityPlaceholder="Select City"
                          isDarkMode={isDarkMode}
                          selectedValueIsLabel={true}
                        />
                        {errors.city && <div style={errorStyle}>{errors.city}</div>}
                      </div>
                      
                      <div className="col-12">
                        <label
                          className={
                            isDarkMode ? "text-white" : "text-gray-700"
                          }
                          style={{ fontWeight: "500" }}
                        >
                          User Roles *
                        </label>
                        <Select
                          value={selectedRole}
                          onChange={(option) => {
                            const newValue = option ? option.value : null;
                            setSelectedRole(option);
                            setStaffData({
                              ...staffData,
                              userRoles: newValue,
                            });
                            handleSelectChange("userRoles", newValue, validateRole);
                          }}
                          options={roles}
                          styles={dropdownStyles}
                          placeholder="Select Role"
                          isClearable={true}
                        />
                        {errors.userRoles && <div style={errorStyle}>{errors.userRoles}</div>}
                      </div>
                      <div className="col-12">
                        <label
                          className={
                            isDarkMode ? "text-white" : "text-gray-700"
                          }
                          style={{ fontWeight: "500" }}
                        >
                          Manager
                        </label>
                        <Select
                          value={selectedManager}
                          onChange={(option) => {
                            const newValue = option ? option.value : "";
                            setSelectedManager(option);
                            setStaffData({
                              ...staffData,
                              managerUserName: newValue,
                            });
                          }}
                          options={managers}
                          styles={dropdownStyles}
                          placeholder="Select Manager"
                          isClearable={true}
                        />
                      </div>
                      <div className="col-12">
                        <FormInput
                          label="Pass Code *"
                          placeHolder="Enter Pass Code"
                          type="text"
                          value={staffData.passCode}
                          onchange={(e) =>
                            handleInputChange("passCode", e.target.value, validatePassCode)
                          }
                        />
                        {errors.passCode && <div style={errorStyle}>{errors.passCode}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="p-4" style={cardStyle}>
                    <h6 style={sectionHeaderStyle}>Compensation</h6>
                    <div className="row g-3">
                      <div className="col-12">
                        <ToggleSwitch
                          isChecked={staffData.isSalaryStaff}
                          onToggle={(checked) =>
                            setStaffData({
                              ...staffData,
                              isSalaryStaff: checked,
                              // Clear opposite fields when toggling (keep overtime in both cases)
                              ratePerHour: checked ? null : staffData.ratePerHour,
                              salary: checked ? staffData.salary : null,
                              salaryType: checked ? staffData.salaryType : "Weekly",
                            })
                          }
                          label="Salary Staff"
                          name="salaryStaff"
                          themeColor={themeColor}
                        />
                      </div>

                      {/* Salary Fields - Show when isSalaryStaff is true */}
                      {staffData.isSalaryStaff && (
                        <>
                          <div className="col-12">
                            <label
                              className={isDarkMode ? "text-white" : "text-gray-700"}
                              style={{ fontWeight: "500" }}
                            >
                              Salary Type *
                            </label>
                            <Select
                              value={
                                staffData.salaryType
                                  ? { value: staffData.salaryType, label: staffData.salaryType }
                                  : null
                              }
                              onChange={(option) => {
                                const newValue = option ? option.value : "Weekly";
                                setStaffData({ ...staffData, salaryType: newValue });
                                handleSelectChange(
                                  "salaryType",
                                  newValue,
                                  (val) => validateSalaryType(val, staffData.isSalaryStaff)
                                );
                                setErrors((prev) => ({
                                  ...prev,
                                  ratePerHour: validateRatePerHour(
                                    staffData.ratePerHour,
                                    staffData.isSalaryStaff,
                                    newValue
                                  ),
                                }));
                              }}
                              options={[
                                { value: "Weekly", label: "Weekly" },
                                { value: "Monthly", label: "Monthly" },
                                { value: "Hourly", label: "Hourly" },
                              ]}
                              styles={dropdownStyles}
                              placeholder="Select Salary Type"
                              isClearable={false}
                            />
                            {errors.salaryType && (
                              <div style={errorStyle}>{errors.salaryType}</div>
                            )}
                          </div>
                          {staffData.salaryType !== "Hourly" && (
                            <div className="col-12">
                              <FormInput
                                label={`${staffData.salaryType} Salary *`}
                                placeHolder={`Enter ${staffData.salaryType} Salary`}
                                type="number"
                                step="0.01"
                                value={staffData.salary || ""}
                                onchange={(e) =>
                                  handleInputChange(
                                    "salary",
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    null,
                                    () => validateSalary(
                                      e.target.value ? parseFloat(e.target.value) : null,
                                      staffData.isSalaryStaff,
                                      staffData.salaryType
                                    )
                                  )
                                }
                              />
                              {errors.salary && (
                                <div style={errorStyle}>{errors.salary}</div>
                              )}
                            </div>
                          )}
                          {staffData.salaryType === "Weekly" && (
                            <div className="col-12">
                              <FormInput
                                label="Weekly Hours *"
                                placeHolder="Enter Weekly Hours"
                                type="number"
                                step="0.01"
                                value={staffData.weeklyHours ?? ""}
                                onchange={(e) =>
                                  handleInputChange(
                                    "weeklyHours",
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    null,
                                    () => validateWeeklyHours(
                                      e.target.value ? parseFloat(e.target.value) : null,
                                      staffData.isSalaryStaff,
                                      staffData.salaryType
                                    )
                                  )
                                }
                              />
                              {errors.weeklyHours && (
                                <div style={errorStyle}>{errors.weeklyHours}</div>
                              )}
                            </div>
                          )}
                          {staffData.salaryType === "Monthly" && (
                            <div className="col-12">
                              <FormInput
                                label="Monthly Hours *"
                                placeHolder="Enter Monthly Hours"
                                type="number"
                                step="0.01"
                                value={staffData.monthlyHours ?? ""}
                                onchange={(e) =>
                                  handleInputChange(
                                    "monthlyHours",
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    null,
                                    () => validateMonthlyHours(
                                      e.target.value ? parseFloat(e.target.value) : null,
                                      staffData.isSalaryStaff,
                                      staffData.salaryType
                                    )
                                  )
                                }
                              />
                              {errors.monthlyHours && (
                                <div style={errorStyle}>{errors.monthlyHours}</div>
                              )}
                            </div>
                          )}
                          {staffData.salaryType === "Hourly" && (
                            <div className="col-12">
                              <FormInput
                                label="Hourly Rate *"
                                placeHolder="Enter Hourly Rate"
                                type="number"
                                step="0.01"
                                value={staffData.ratePerHour ?? ""}
                                onchange={(e) =>
                                  handleInputChange(
                                    "ratePerHour",
                                    e.target.value ? parseFloat(e.target.value) : null,
                                    null,
                                    () =>
                                      validateRatePerHour(
                                        e.target.value ? parseFloat(e.target.value) : null,
                                        staffData.isSalaryStaff,
                                        staffData.salaryType
                                      )
                                  )
                                }
                              />
                              {errors.ratePerHour && (
                                <div style={errorStyle}>{errors.ratePerHour}</div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Overtime - Always visible for both salary and hourly staff */}
                      <div className="col-12">
                        <ToggleSwitch
                          isChecked={staffData.isAllowOverTime}
                          onToggle={(checked) =>
                            setStaffData({
                              ...staffData,
                              isAllowOverTime: checked,
                              overTimeHourlyRate: checked ? staffData.overTimeHourlyRate || null : null,
                            })
                          }
                          label="Allow Overtime"
                          name="allowOvertime"
                          themeColor={themeColor}
                        />
                        {staffData.isAllowOverTime && (
                          <div className="mt-3">
                            <FormInput
                              label="Overtime Hourly Rate"
                              placeHolder="Enter Overtime Rate"
                              type="number"
                              step="0.01"
                              value={staffData.overTimeHourlyRate || ""}
                              onchange={(e) =>
                                handleInputChange(
                                  "overTimeHourlyRate",
                                  e.target.value ? parseFloat(e.target.value) : null,
                                  null,
                                  () => validateOverTimeRate(e.target.value ? parseFloat(e.target.value) : null, staffData.isAllowOverTime, staffData.ratePerHour)
                                )
                              }
                            />
                            {errors.overTimeHourlyRate && <div style={errorStyle}>{errors.overTimeHourlyRate}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default UpdateStaff;